import React, { useEffect, useState } from 'react';
import {
    Button, Card, CardBody, CardHeader, Col, Container, Row, Modal, ModalHeader, ModalBody, FormGroup, Label, Input
} from 'reactstrap';
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import Pagination from "../../../components/Pagination/Pagination";
import { confirmDelete, createOrUpdateRole, getRoles as fetchRoles } from "../Roles/RoleService";
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { getRole, getRolePermissions } from "../../../services/ValitanApiService";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";  // ✅ add navigation

const RoleListTables = () => {
    document.title = "Roles | Valitan";

    const [roles, setRoles] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [roleName, setRoleName] = useState('');
    const [roleCode, setRoleCode] = useState('');
    const [errors, setErrors] = useState({ roleName: '', roleCode: '' });
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [viewPermissionsOpen, setViewPermissionsOpen] = useState(false);
    const [viewingRoleName, setViewingRoleName] = useState("");
    const [assignedPermissions, setAssignedPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState({
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
        sort: {}
    });

    const navigate = useNavigate(); // ✅ for routing

    useEffect(() => {
        loadRoles(0, paginationInfo.pageSize);
    }, [paginationInfo.pageSize]);

    const loadRoles = async (page = 0, size = 10) => {
        const { roles, paginationInfo } = await fetchRoles(page, size);
        setRoles(roles);
        setPaginationInfo(paginationInfo);
    };

    const handleNextPage = () => {
        if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
            loadRoles(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
        }
    };

    const handlePreviousPage = () => {
        if (paginationInfo.pageNumber > 0) {
            loadRoles(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
        }
    };

    const openPermissionsModal = async (role) => {
        try {
            setPermissionsLoading(true);
            setViewingRoleName(role.name);
            setViewPermissionsOpen(true);

            const response = await getRolePermissions(role.id, 0, 1000);
            const content = response?.data?.data?.content || [];
            setAssignedPermissions(content);
        } catch (error) {
            console.error("Error fetching assigned permissions:", error);
            setAssignedPermissions([]);
        } finally {
            setPermissionsLoading(false);
        }
    };

    const closePermissionsModal = () => {
        setViewPermissionsOpen(false);
        setViewingRoleName("");
        setAssignedPermissions([]);
    };

    const openModalForEdit = async (id) => {
        setEditingRoleId(id);
        try {
            const response = await getRole(id);
            if (response) {
                setRoleName(response.data.name);
                setRoleCode(response.data.code);
                setModalOpen(true);
            } else {
                console.error("Role data is undefined");
            }
        } catch (error) {
            console.error("Error fetching role data:", error);
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (!modalOpen) {
            setRoleName('');
            setRoleCode('');
            setErrors({ roleName: '', roleCode: '' });
            setEditingRoleId(null);
        }
    };

    const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Roles management" breadcrumbItem="Roles" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">
                                        <Button color="primary" className="add-btn float-end" id="TooltipTop"
                                            onClick={toggleModal}>
                                            <i className="ri-add-line align-bottom me-1"></i> Add
                                        </Button>
                                    </h4>
                                </CardHeader>

                                <CardBody>
                                    <div>
                                        <Row className="g-4 mb-3">
                                            <Col className="col-sm">
                                                <div className="d-flex justify-content-sm-end">
                                                    <div className="search-box ms-2">
                                                        <input type="text" className="form-control search"
                                                            placeholder="Search..." />
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="table-responsive table-card mt-3 mb-1">
                                            <table className="table align-middle table-nowrap" id="customerTable">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Name</th>
                                                        <th>Code</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {roles.length > 0 ? (
                                                        roles.map((role, index) => (
                                                            <tr key={role.id}>
                                                                <td>{startIndex + index}</td>
                                                                <td>{role.name}</td>
                                                                <td>{role.code}</td>
                                                                <td>
                                                                    <div className="btn-group" role="group" aria-label="Role Actions">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-info btn-sm"
                                                                            onClick={() => openPermissionsModal(role)}
                                                                        >
                                                                            <i className="ri-eye-line"></i>
                                                                        </button>

                                                                        {/* Edit */}
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-primary btn-sm"
                                                                            onClick={() => openModalForEdit(role.id)}
                                                                        >
                                                                            <i className="ri-edit-box-line"></i>
                                                                        </button>

                                                                        {/* Delete */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => confirmDelete(role.id, loadRoles)}
                                                                            className="btn btn-danger btn-sm"
                                                                        >
                                                                            <i className="ri-delete-bin-line"></i>
                                                                        </button>

                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-warning btn-sm"
                                                                            onClick={() => navigate(`/roles/${role.id}/permissions`)}
                                                                        >
                                                                            <i className="ri-lock-line"></i>
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4" style={{ textAlign: 'center' }}>No records found</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <Pagination
                                            paginationInfo={paginationInfo}
                                            handleNextPage={handleNextPage}
                                            handlePreviousPage={handlePreviousPage}
                                            onPageChange={(pageNumber, pageSize) => loadRoles(pageNumber, pageSize)}
                                            onPageSizeChange={(newPageSize) => {
                                                setPaginationInfo((prev) => ({
                                                    ...prev,
                                                    pageSize: newPageSize,
                                                    pageNumber: 0,
                                                }));
                                            }}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* ConfirmDialog */}
            <ConfirmDialog />

            {/* Modal for Add/Edit */}
            <Modal isOpen={modalOpen} toggle={toggleModal} backdrop="static">
                <ModalHeader toggle={toggleModal}>
                    {editingRoleId ? 'Edit Role' : 'Add Role'}
                </ModalHeader>
                <ModalBody>
                    <form
                        onSubmit={(evt) =>
                            createOrUpdateRole(evt, roleName, roleCode, editingRoleId, loadRoles, toggleModal, setErrors)
                        }>
                        <FormGroup>
                            <Label for="roleName">Name</Label>
                            <Input
                                type="text"
                                id="roleName"
                                value={roleName}
                                onChange={(e) => setRoleName(e.target.value)}
                                invalid={!!errors.roleName}
                            />
                            {errors.roleName && <div className="invalid-feedback">{errors.roleName}</div>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="roleCode">Code</Label>
                            <Input
                                type="text"
                                id="roleCode"
                                value={roleCode}
                                onChange={(e) => setRoleCode(e.target.value)}
                                invalid={!!errors.roleCode}
                            />
                            {errors.roleCode && <div className="invalid-feedback">{errors.roleCode}</div>}
                        </FormGroup>
                        <Button type="submit" color="primary" className="float-end">
                            {editingRoleId ? 'Update' : 'Save'}
                        </Button>
                    </form>
                </ModalBody>
            </Modal>

            <Modal isOpen={viewPermissionsOpen} toggle={closePermissionsModal} size="lg">
                <ModalHeader toggle={closePermissionsModal}>
                    Assigned Permissions: {viewingRoleName}
                </ModalHeader>
                <ModalBody>
                    {permissionsLoading ? (
                        <div className="text-center py-4">Loading assigned permissions...</div>
                    ) : assignedPermissions.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table align-middle table-nowrap mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Controller</th>
                                        <th>Method</th>
                                        <th>Endpoint</th>
                                        <th>Scope</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignedPermissions.map((item, index) => (
                                        <tr key={item.id || `${item.permission?.id}-${index}`}>
                                            <td>{index + 1}</td>
                                            <td>{item.permission?.controller || "-"}</td>
                                            <td>{item.permission?.method || "-"}</td>
                                            <td>{item.permission?.end_point || "-"}</td>
                                            <td>{item.scope || "ALL"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center text-muted py-4">
                            No permissions assigned to this role.
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default RoleListTables;
